"use client"

import type React from "react"
import { StudentSignupModal } from "@/components/student-signup-modal"
import { createClient } from "@/lib/supabase/client"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { WaLoginForm } from "@/components/wa-login-form"
import { Mail, MessageCircle } from "lucide-react"
import { useRouter } from 'next/navigation'
import { cheminRetourSur } from '@/lib/auth/session-expiree'
import { destinationApresConnexion } from '@/lib/auth/destination-connexion'
import { useState } from "react"
import { loginWithIdentifier } from '@/app/actions/auth'

export default function LoginPage() {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [showSignupModal, setShowSignupModal] = useState(false)
  const router = useRouter()

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    const supabase = createClient()
    setIsLoading(true)
    setError(null)

    try {
      console.log('[v0] Starting login with identifier:', email)
      
      const result = await loginWithIdentifier(email, password)
      
      if (!result.success || !result.email) {
        setError('Erreur lors de la préparation de la connexion')
        setIsLoading(false)
        return
      }

      const authEmail = result.email
      console.log('[v0] Attempting login with email:', authEmail)

      const { data, error } = await supabase.auth.signInWithPassword({
        email: authEmail,
        password,
        options: {
          emailRedirectTo: process.env.NEXT_PUBLIC_DEV_SUPABASE_REDIRECT_URL || `${window.location.origin}/chat`,
        },
      })

      if (error) {
        throw error
      }

      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", data.user?.id)
        .single()

      if (profileError && profileError.code === "PGRST116") {
        setError("Votre profil n'existe pas. Veuillez contacter l'administrateur ou créer un nouveau compte.")
        return
      }

      // Formateur KIDSHAKER en école (ou administrateur) : il arrive directement sur « Ma classe ».
      // Retour sur la page quittée à l'expiration de session (ex. /assistant), chemin interne seulement.
      const retour = cheminRetourSur(new URLSearchParams(window.location.search).get("redirect"))
      router.push(retour ?? destinationApresConnexion(profileData?.role, "/chat", profileData?.declic_enabled, profileData?.is_admin))
      router.refresh()
    } catch (error: unknown) {
      if (error instanceof Error) {
        if (error.message.includes("Invalid login credentials")) {
          setError("Email ou mot de passe incorrect")
        } else if (error.message.includes("Email not confirmed")) {
          setError("Veuillez confirmer votre email avant de vous connecter")
        } else {
          setError(error.message)
        }
      } else {
        setError("Une erreur s'est produite lors de la connexion")
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="flex min-h-svh w-full items-center justify-center p-6 md:p-10 bg-gradient-to-br from-blue-50 to-indigo-50">
      <div className="w-full max-w-md">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col items-center gap-2 text-center">
            <div className="flex items-center gap-2 text-2xl font-bold text-indigo-600">
              <svg
                className="h-8 w-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                />
              </svg>
              Tuteur IA
            </div>
            <p className="text-sm text-muted-foreground">Votre assistant d'apprentissage intelligent</p>
          </div>

          {/* Email/Password Login */}
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">Connexion</CardTitle>
              <CardDescription>Connecte-toi avec ton email, ton téléphone ou ton identifiant</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="email">
                <TabsList className="grid w-full grid-cols-2 mb-4">
                  <TabsTrigger value="email" className="gap-1.5">
                    <Mail className="h-4 w-4" aria-hidden="true" />
                    Email
                  </TabsTrigger>
                  <TabsTrigger value="whatsapp" className="gap-1.5">
                    <MessageCircle className="h-4 w-4" aria-hidden="true" />
                    WhatsApp
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="email">
              <form onSubmit={handleLogin}>
                <div className="flex flex-col gap-6">
                  <div className="grid gap-2">
                    <Label htmlFor="email">Email, téléphone ou identifiant</Label>
                    <Input
                      id="email"
                      type="text"
                      autoComplete="username"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      placeholder="email, 06 12 34 56 78 ou identifiant"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="password">Mot de passe</Label>
                    <Input
                      id="password"
                      type="password"
                      autoComplete="current-password"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>
                  {error && (
                    <div className="rounded-md bg-red-50 p-3 text-sm text-red-800 border border-red-200">{error}</div>
                  )}
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? "Connexion..." : "Se connecter"}
                  </Button>
                </div>
                <div className="mt-4 text-center text-sm">
                  <a href="/connexion-formateur" className="underline underline-offset-4 text-indigo-600 hover:text-indigo-700">
                    Formateur KIDSHAKER : je n&apos;ai plus mon mot de passe
                  </a>
                </div>
                <div className="mt-2 text-center text-sm">
                  Pas encore de compte ?{" "}
                  <button
                    type="button"
                    onClick={() => setShowSignupModal(true)}
                    className="underline underline-offset-4 text-indigo-600 hover:text-indigo-700"
                  >
                    Créer un compte
                  </button>
                </div>
              </form>
                </TabsContent>
                <TabsContent value="whatsapp">
                  <WaLoginForm onSuccess={() => {}} />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
      <StudentSignupModal open={showSignupModal} onOpenChange={setShowSignupModal} />
    </div>
  )
}
